import { Button, FormHelperText, Icon, Typography } from "@mui/material";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useMediaQuery } from "react-responsive";
import CreativeChannelsModalBase from "@/components/modals/CreativeChannelsModal";
import CreativeChannelsBase from "./CreativeChannels";
import CreativeOptionsBase from "./CreativeOptions";
import { GlobalContext } from "@/helpers/contexts/contexts";
import RpPopoverBase from "@/components/popover/RpPopover";
import RpMUIDialogBase from "@/components/modals/RpMUIModal";
import ParticipantFeedbackBase from "../ParticipantFeedback";
import AdvanceSettingFormBase from "@/features/campaigns/shared/AdvanceSettingForm";
import useIsParticipant from "@/features/auth/hooks/useIsParticipant";
import { getCreativePackageLabel } from "../../helpers/creativeLabel";
import {
  canSelectPackageWithEnabledChannels,
  getBlockedPackageSelectionInfo,
  getChannelsBlockedFromDisabling,
  getPackageChannelOptions,
  isParticipantChannelSelectionEnabled,
} from "@/features/campaigns/shared/helpers/channelSelectionHelper";
import ChannelFilterSwitches from "./ChannelFilterSwitches";

type CreativeId = string | number;

type CampaignData = {
  creative_select_flag?: boolean;
  creative_select_max_value?: number;
  can_participant_select_specific_channels?: boolean;
  campaign_advanced_setting?: {
    is_required?: boolean;
  };
  [key: string]: unknown;
};

type LocationData = {
  radius?: unknown;
  [key: string]: unknown;
};

type CreativePackage = {
  creative_id?: CreativeId;
  creative_name?: string;
  is_recommended?: boolean;
  package_channels?: Array<Record<string, unknown>>;
  [key: string]: unknown;
};

type AdvancedSettingData = {
  settings?: unknown[];
  [key: string]: unknown;
};

type CreativesProps = {
  campaign?: CampaignData;
  creatives?: CreativePackage[];
  initiallySelected?: CreativeId[];
  loc?: LocationData;
  locationName?: string;
  onSelectCreative?: (_selectedCreatives: CreativeId[]) => void;
  productList?: unknown;
  getAdvanceSettingData?: AdvancedSettingData;
  onFillSettingsData?: (..._args: unknown[]) => void;
  initialAnswers?: unknown;
  initialDisabled?: boolean;
  helperText?: React.ReactNode;
  selectedChannelIds?: Array<string | number>;
  onSelectChannels?: (..._args: unknown[]) => void;
};

const CreativeChannelsModal =
  CreativeChannelsModalBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;
const CreativeOptions = CreativeOptionsBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const CreativeChannels = CreativeChannelsBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const RpPopover = RpPopoverBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const RpMUIDialog = RpMUIDialogBase as unknown as React.ComponentType<
  Record<string, unknown>
>;
const ParticipantFeedback =
  ParticipantFeedbackBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;
const AdvanceSettingForm =
  AdvanceSettingFormBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;

export default function Creatives({
  campaign,
  creatives = [],
  initiallySelected = [],
  loc,
  locationName = "",
  onSelectCreative,
  productList,
  getAdvanceSettingData,
  onFillSettingsData,
  initialAnswers,
  initialDisabled,
  helperText = "",
  selectedChannelIds = [],
  onSelectChannels,
}: CreativesProps) {
  const { selectedBrand } = useContext(GlobalContext) as {
    selectedBrand?: { id?: number };
  };
  const isParticipant = useIsParticipant();
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 766px)" });

  const [feedback, setFeedback] = useState(false);

  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => {
    if (initiallySelected && initiallySelected.length) {
      return initiallySelected[0];
    }
    return creatives && creatives.length ? creatives[0].creative_id : 0;
  });

  const [openAdvancedSetting, setAdvancedSetting] = useState(false);
  const [selectedCount, setSelectedCount] = useState(
    initiallySelected?.length || 0
  );

  const settingsData = getAdvanceSettingData?.settings;
  const channelSelectionEnabled =
    isParticipantChannelSelectionEnabled(campaign);
  const channelOptions = useMemo(() => {
    return getPackageChannelOptions(creatives);
  }, [creatives]);
  const getCreativeOptions = useMemo(() => {
    return creatives.map((c) => {
      return {
        label: getCreativePackageLabel(c),
        value: c.creative_id,
        is_recommended: c.is_recommended,
      };
    });
  }, [creatives]);
  const blockedChannelIds = useMemo(() => {
    if (!channelSelectionEnabled) {
      return [];
    }

    return getChannelsBlockedFromDisabling({
      creatives,
      selectedPackageIds: initiallySelected,
      selectedChannelIds,
    });
  }, [
    channelSelectionEnabled,
    creatives,
    initiallySelected,
    selectedChannelIds,
  ]);
  const canSelectCreativePackage = useCallback(
    (creativeId: CreativeId) => {
      if (!channelSelectionEnabled) {
        return true;
      }

      const canSelect = canSelectPackageWithEnabledChannels({
        creatives,
        packageId: creativeId,
        selectedChannelIds,
      });

      return canSelect
        ? true
        : (() => {
            const blockedPackageInfo = getBlockedPackageSelectionInfo({
              creatives,
              packageId: creativeId,
            });
            const creativeOptionLabel = getCreativeOptions.find(
              (creativeOption) =>
                String(creativeOption.value) === String(creativeId)
            )?.label;

            return {
              ...blockedPackageInfo,
              packageName:
                creativeOptionLabel || blockedPackageInfo.packageName,
            };
          })();
    },
    [channelSelectionEnabled, creatives, getCreativeOptions, selectedChannelIds]
  );

  const handleCloseModal = () => {
    setAdvancedSetting(false);
  };

  useEffect(() => {
    if (!isTabletOrMobile) {
      setOpen(false);
    }
  }, [isTabletOrMobile]);

  const toViewCreativeName = useMemo(() => {
    const creative = creatives.find((c) => c.creative_id === view);
    return creative ? getCreativePackageLabel(creative) : "";
  }, [view, creatives]);

  const handleOnClickView = (v: CreativeId) => {
    setView(v);
    if (isTabletOrMobile) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const handleOnSelect = (selectedCreatives: CreativeId[]) => {
    setSelectedCount(selectedCreatives?.length || 0);
    onSelectCreative?.(selectedCreatives);
  };

  const creativeName = useMemo(() => {
    const creative = creatives.find((c) => c.creative_id === view);
    return creative ? getCreativePackageLabel(creative) : "";
  }, [creatives, view]);

  const renderAdvancedSetting = () => {
    if (!getAdvanceSettingData) {
      return null;
    }

    if (campaign?.campaign_advanced_setting?.is_required) {
      return null;
    }

    return (
      <div className="row">
        <div className="col-12">
          <Button
            color="primary"
            size="small"
            onClick={() => {
              setAdvancedSetting(true);
            }}
            className="mt-3"
          >
            Advanced Settings
          </Button>
        </div>
        <RpMUIDialog
          open={openAdvancedSetting}
          onClose={handleCloseModal}
          maxWidth="md"
        >
          <AdvanceSettingForm
            settingsData={settingsData}
            onClose={handleCloseModal}
            onFillSettingsData={onFillSettingsData}
            productList={productList}
            initialAnswers={initialAnswers}
            locationName={locationName}
            locationRadius={loc?.radius}
          />
        </RpMUIDialog>
      </div>
    );
  };

  return (
    <div className="creatives">
      <div className="card border-0">
        <div className="card-body p-0">
          <div className="row">
            <div className="col-md-5 col-lg-4 col-xl-3 pr-md-0 d-flex flex-column justify-content-between">
              <div>
                <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
                  <Typography
                    component="h6"
                    variant="body2"
                    gutterBottom
                    className="fw-sb"
                  >
                    {`Select ${
                      campaign?.creative_select_flag
                        ? `up to ${campaign?.creative_select_max_value}`
                        : "One"
                    }`}
                  </Typography>
                  {campaign?.creative_select_flag && (
                    <Typography
                      component="h6"
                      variant="body2"
                      gutterBottom
                      className="fw-sb m-2"
                      sx={{
                        color: "var(--neutral-700)",
                      }}
                    >
                      {`Selected ${selectedCount}/${campaign?.creative_select_max_value}`}
                    </Typography>
                  )}
                </div>
                <CreativeOptions
                  creativesOptions={getCreativeOptions}
                  initiallySelected={initiallySelected}
                  onClickView={handleOnClickView}
                  onSelect={handleOnSelect}
                  currentInView={view}
                  isTabletOrMobile={isTabletOrMobile}
                  creative_select_flag={campaign?.creative_select_flag}
                  creative_select_max_value={
                    campaign?.creative_select_max_value
                  }
                  initialDisabled={initialDisabled}
                  canSelectCreative={canSelectCreativePackage}
                />
                {helperText && (
                  <FormHelperText error>{helperText}</FormHelperText>
                )}
              </div>

              <div>
                {settingsData?.length ? <>{renderAdvancedSetting()}</> : null}
                {selectedBrand?.id !== 3 &&
                creativeName &&
                !initialDisabled &&
                isParticipant ? (
                  <div className="my-2">
                    <RpPopover popContent="Provide Feedback">
                      <Button
                        size="small"
                        startIcon={<Icon color="primary">feedback</Icon>}
                        onClick={() => setFeedback(true)}
                      >
                        Feedback
                      </Button>
                    </RpPopover>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="col-md-7 col-lg-8 col-xl-9 pl-md-0 d-none d-md-block">
              <CreativeChannels
                loc={loc}
                view={view}
                creatives={creatives}
                campaign={campaign}
                channelSelectionEnabled={channelSelectionEnabled}
                selectedChannelIds={selectedChannelIds}
              />
            </div>
          </div>
        </div>
      </div>
      {channelSelectionEnabled && channelOptions.length ? (
        <div className="creative-channel-filter-row">
          <ChannelFilterSwitches
            channels={channelOptions}
            selectedChannelIds={selectedChannelIds}
            blockedChannelIds={blockedChannelIds}
            lockLastSelectedChannel={Boolean(initiallySelected?.length)}
            helperText="Channels not selected won’t run as part of the package. Only selected channels will run, and at least one channel must remain selected."
            onChange={onSelectChannels}
          />
        </div>
      ) : null}
      {open && (
        <CreativeChannelsModal
          open={open}
          onClose={() => setOpen(false)}
          locationName={locationName}
          creativeName={toViewCreativeName}
        >
          <div className="px-sm-3">
            <CreativeChannels
              loc={loc}
              view={view}
              creatives={creatives}
              campaign={campaign}
              channelSelectionEnabled={channelSelectionEnabled}
              selectedChannelIds={selectedChannelIds}
            />
          </div>
        </CreativeChannelsModal>
      )}

      <RpMUIDialog
        open={feedback}
        onClose={() => setFeedback(false)}
        fullWidth
        maxWidth="md"
        heading="Creative feedback"
      >
        <ParticipantFeedback
          close={() => setFeedback(false)}
          creativeId={view}
          creativeName={creativeName}
          campaign={campaign}
          location={loc}
        />
      </RpMUIDialog>
    </div>
  );
}
